// helix-exception: Helix has no semantic token for these hues (Figma needs-input orange, highlight yellow/amber, categorical teal/cyan); primitive ramps with the identical colour are used (F-11)
// helix-exception: inline text highlight marks keep their 1.15px/3px mark radii; no Helix radius token fits mark geometry (F-12)
import { forwardRef, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { HighlightTone } from './clinicalSummaryEditorHighlightSpans'
import {
  placeTooltip,
  type ClinicalSummaryTooltipFooter,
  type ClinicalSummaryTooltipLayout,
  type ClinicalSummaryTooltipModel,
  type TooltipAnchorRect,
  type TooltipViewportPosition,
} from './clinicalSummaryTooltipModel'

/** The summary's mark fills, with Figma's real 2px padding (the summary fakes it with a spread shadow). */
const quoteMarkToneClass: Record<HighlightTone, string> = {
  key: 'bg-background-brand-soft dark:bg-gray-800',
  additional: 'bg-background-brand-soft dark:bg-gray-800',
  excluded: 'bg-error-100 dark:bg-gray-900',
  'needs-input': 'bg-yellow-200 dark:bg-yellow-900/40 shadow-[inset_0_0_0_1.5px_var(--color-orange-500)]',
}

const META_CLASS = 'text-xs font-medium leading-4 text-text-subtle dark:text-gray-500'

export interface ClinicalSummaryHoverTooltipProps {
  model: ClinicalSummaryTooltipModel
  quote?: boolean
  footer?: ClinicalSummaryTooltipFooter
}

function footerText(model: ClinicalSummaryTooltipModel, footer: ClinicalSummaryTooltipFooter): string | null {
  if (footer === 'mentions') {
    return `${model.mentionCount} mention${model.mentionCount === 1 ? '' : 's'} in the summary`
  }
  return model.foundAsPhrases.length > 0 ? `Found as: ${model.foundAsPhrases.join(', ')}` : null
}

/** Dark card shared by the highlight (R07), row and chip (R08, S02) tooltips. */
export const ClinicalSummaryHoverTooltip = forwardRef<HTMLDivElement, ClinicalSummaryHoverTooltipProps>(
  function ClinicalSummaryHoverTooltip({ model, quote = false, footer = 'found-as' }, ref) {
    const snippet = quote ? model.contextSnippet : null
    const meta = model.mentionCount > 0 ? footerText(model, footer) : null
    return (
      <div
        ref={ref}
        className="w-[380px] max-w-[calc(100vw-16px)] rounded-base bg-background-dark px-3 py-2 text-left drop-shadow-xl"
      >
        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 break-words text-sm font-medium leading-5 text-white">{model.termLabel}</p>
          <p className="shrink-0 whitespace-nowrap text-xs font-medium leading-4 text-text-subtle dark:text-gray-500">{model.hpoCode}</p>
        </div>
        <p className="text-xs font-normal leading-5 text-white/80">{model.categoryLabel}</p>
        {snippet ? (
          <p className="whitespace-normal break-words pt-1.5 text-xs font-normal leading-5 text-white/90">
            {snippet.before}
            <mark className={`inline-block rounded-[3px] p-0.5 text-text-heading dark:text-white ${quoteMarkToneClass[model.tone]}`}>
              {snippet.match}
            </mark>
            {snippet.after}
          </p>
        ) : null}
        {model.mentionCount > 0 ? (
          <div>
            <p className={META_CLASS}>Found in the summary</p>
            {meta ? <p className={`${META_CLASS} break-words`}>{meta}</p> : null}
          </div>
        ) : null}
      </div>
    )
  }
)

export interface ClinicalSummaryHoverTooltipPortalProps {
  /** Referenced by the anchor's `aria-describedby`. */
  id: string
  anchor: TooltipAnchorRect | null
  model: ClinicalSummaryTooltipModel | null
  layout: ClinicalSummaryTooltipLayout
}

/** Non-interactive tooltip in a portal, placed against `anchor` (viewport coordinates) once measured. */
export function ClinicalSummaryHoverTooltipPortal({ id, anchor, model, layout }: ClinicalSummaryHoverTooltipPortalProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<TooltipViewportPosition | null>(null)

  useLayoutEffect(() => {
    const card = cardRef.current
    if (!anchor || !card) return
    const { width, height } = card.getBoundingClientRect()
    const next = placeTooltip(anchor, layout, { width, height }, { width: window.innerWidth, height: window.innerHeight })
    setPosition((current) => (current && current.top === next.top && current.left === next.left ? current : next))
  }, [anchor, model, layout])

  if (!anchor || !model || typeof document === 'undefined') return null

  return createPortal(
    <div
      id={id}
      role="tooltip"
      className={`pointer-events-none fixed z-50 w-max${position ? '' : ' invisible'}`}
      style={position ?? { top: 0, left: 0 }}
    >
      <ClinicalSummaryHoverTooltip ref={cardRef} model={model} quote={layout.quote} footer={layout.footer} />
    </div>,
    document.body
  )
}
